/**
 * Structured error helper for the SDK.
 *
 * The parser API returns a free-form `msg` string (no formal error code
 * vocabulary). This module turns errors into a small, evidence-based
 * envelope that's safe to surface to end users:
 *
 *   - Status-code-derived codes (`AUTHENTICATION`, `RATE_LIMITED`,
 *     `NETWORK`) are reliable — they're driven by HTTP semantics that
 *     `api.ts` actually returns.
 *   - Source-of-error codes (`PARSE_ERROR`, `GENERATOR_ERROR`,
 *     `CDSL_FETCH_ERROR`) are passed in as `fallbackCode` by the caller
 *     when classification fails — these match the v1.x contract and are
 *     the most common codes embedders will see.
 *   - `INVALID_PASSWORD` is detected only when the API response message
 *     mentions the word "password" — narrow, intentionally conservative
 *     so we don't mis-blame the user for an unrelated server error.
 *   - `UNKNOWN` covers everything else.
 *
 * We deliberately do NOT try to infer fancy categories like
 * "tampered PDF", "corrupt scan", "quota exceeded", or "unsupported
 * provider" by substring-matching the API's English error text. Those
 * heuristics were too fragile to be useful and too easy to false-match,
 * so the previous version's expanded taxonomy was removed.
 *
 * If you need richer per-failure UX, inspect `ClassifiedError.raw` in
 * your `onError` callback — it contains the original API payload.
 */

export type ErrorCode =
  | 'INVALID_PASSWORD'
  | 'NETWORK'
  | 'RATE_LIMITED'
  | 'AUTHENTICATION'
  | 'PARSE_ERROR'
  | 'GENERATOR_ERROR'
  | 'CDSL_FETCH_ERROR'
  | 'INBOX_ERROR'
  | 'INBOUND_EMAIL_ERROR'
  | 'UNKNOWN';

export interface ClassifiedError {
  code: ErrorCode;
  /** Short, user-readable headline (e.g. for a banner title). */
  title: string;
  /** Slightly longer description suitable for the body of an error view. */
  message: string;
  /** Optional "what to try next" hint shown beneath the message. */
  remediation?: string;
  /** Whether retrying the same action is likely to help. */
  retryable: boolean;
  /** Original API/network payload — keep for analytics or your own UI. */
  raw?: unknown;
}

/**
 * Classify an error thrown by the SDK (fetch errors, or anything with a
 * `response.data.msg` shape produced by `api.ts`) into a `ClassifiedError`.
 *
 * Pass a `fallbackCode` matching the call site so unclassified errors get
 * a useful code (e.g. `'CDSL_FETCH_ERROR'` from CDSL handlers).
 */
export function classifyError(
  input: unknown,
  fallbackCode: ErrorCode = 'UNKNOWN',
): ClassifiedError {
  const err = input as any;
  const raw = err?.response?.data ?? err?.data ?? err;

  const status: number | undefined =
    err?.response?.status || err?.status || raw?.status_code;
  const msg: string =
    (typeof raw === 'string' ? raw : raw?.msg || raw?.message) ||
    err?.message ||
    '';
  const lower = msg.toLowerCase();

  // ── Auth (HTTP 401/403) ──────────────────────────────────────────
  // Status-code based — reliable across any backend behaviour change.
  if (status === 401 || status === 403) {
    return {
      code: 'AUTHENTICATION',
      title: 'Session expired',
      message: 'Your session has expired or this app isn\'t set up correctly.',
      remediation: 'Please close this window and try again from the start.',
      retryable: false,
      raw,
    };
  }

  // ── Rate limiting (HTTP 429) ─────────────────────────────────────
  if (status === 429) {
    return {
      code: 'RATE_LIMITED',
      title: 'Too many requests',
      message: 'We\'ve received a lot of traffic in a short time.',
      remediation: 'Wait a few seconds and try again.',
      retryable: true,
      raw,
    };
  }

  // ── Network / server failure ─────────────────────────────────────
  // Covers HTTP 5xx and the standard fetch/connectivity error shapes.
  if (
    err?.code === 'ERR_NETWORK' ||
    err?.name === 'NetworkError' ||
    err?.name === 'TypeError' || // fetch() throws TypeError on network failure
    (status !== undefined && status >= 500)
  ) {
    return {
      code: 'NETWORK',
      title: 'Connection problem',
      message: 'We couldn\'t reach the parsing service.',
      remediation: 'Check your internet connection and try again.',
      retryable: true,
      raw,
    };
  }

  // ── Wrong PDF password ───────────────────────────────────────────
  // Narrow heuristic: only when the API message mentions "password"
  // explicitly. This is the single inferred code we keep because it
  // unlocks an important UX (re-prompting on the password field).
  if (lower.includes('password')) {
    return {
      code: 'INVALID_PASSWORD',
      title: 'Password didn\'t work',
      message: 'We couldn\'t unlock the PDF with that password.',
      remediation:
        'Try your PAN in UPPERCASE (e.g. ABCDE1234F), or your date of birth as DDMMYYYY.',
      retryable: true,
      raw,
    };
  }

  // ── Fallback ─────────────────────────────────────────────────────
  // Use the caller-supplied fallback code (PARSE_ERROR / GENERATOR_ERROR
  // / CDSL_FETCH_ERROR / UNKNOWN). Prefer the API's `msg` if present —
  // it's already user-readable in most cases.
  return {
    code: fallbackCode,
    title: 'Something went wrong',
    message: msg || 'We ran into an unexpected error.',
    remediation:
      'Please try again in a moment. If it keeps happening, please reach out to the app you\'re using.',
    retryable: true,
    raw,
  };
}
